import React, { useMemo, useState } from "react";
import {
  Typography,
  TextField,
  InputAdornment,
  Stack,
  Chip,
  Box,
  MenuItem,
  Skeleton,
  Autocomplete,
  Button,
  useMediaQuery,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import LeaderboardOutlinedIcon from "@mui/icons-material/LeaderboardOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import CloseIcon from "@mui/icons-material/Close";
import SwapVertOutlinedIcon from "@mui/icons-material/SwapVertOutlined";

import PedidosVentaMobile from "./PedidosVentaMobile";
import PedidosVentaTableDesktop from "./PedidosVentaTableDesktop";
import PedidoVentaDocDialog from "./PedidoVentaDocDialog";
import {
  EMPRESA_OPTIONS,
  getYearOptions,
  summarizePedidos,
  formatPedidoAmount,
  formatProfitPct,
  filterPedidosVenta,
  listPedidoClientes,
  createPedidoFocus,
  togglePedidoFocus,
  rowMatchesPedidoFocus,
  describePedidoFocus,
  sortPedidosVenta,
  annotatePedidosTipocontRoles,
  PEDIDO_VENTA_ORDEN_DEFAULT,
  PEDIDO_VENTA_ORDEN_OPTIONS,
} from "./pedidosVentaUtils";
import {
  pageHeroShellSx,
  pageContentCardSx,
  pageFieldSx,
} from "../../styles/pageModernStyles";

const ESTADO_OPTIONS = [
  { value: "todos", label: "Todos" },
  { value: "abiertos", label: "Abiertos" },
  { value: "servidos", label: "Servidos" },
];

const DOC_KIND_OPTIONS = [
  { value: "todos", label: "Pedido + factura" },
  { value: "orders", label: "Pedido" },
  { value: "invoices", label: "Factura" },
];

const TablaCompleta = ({
  rows = [],
  loading,
  theme,
  year,
  setYear,
  empresa = "stage",
  onEmpresaChange,
}) => {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"));
  const accent = theme.palette.primary.main;

  const [search, setSearch] = useState("");
  const [estado, setEstado] = useState("todos");
  const [cliente, setCliente] = useState("");
  const [docKind, setDocKind] = useState("todos");
  const [showParteDirecto, setShowParteDirecto] = useState(false);
  const [orden, setOrden] = useState(PEDIDO_VENTA_ORDEN_DEFAULT);
  const [focus, setFocus] = useState(null);
  const [docRow, setDocRow] = useState(null);

  const annotatedRows = useMemo(
    () => annotatePedidosTipocontRoles(rows),
    [rows],
  );
  const clientes = useMemo(
    () => listPedidoClientes(annotatedRows),
    [annotatedRows],
  );

  const filteredRows = useMemo(
    () =>
      sortPedidosVenta(
        filterPedidosVenta(annotatedRows, {
          search,
          estado,
          cliente,
          docKind,
          showParteDirecto,
        }),
        orden,
      ),
    [annotatedRows, search, estado, cliente, docKind, showParteDirecto, orden],
  );

  const parteHiddenCount = useMemo(
    () => annotatedRows.filter((row) => row._tipocontRole === "parte").length,
    [annotatedRows],
  );

  const summary = useMemo(() => summarizePedidos(filteredRows), [filteredRows]);
  const focusedCount = useMemo(
    () =>
      filteredRows.filter((row) => rowMatchesPedidoFocus(row, focus)).length,
    [filteredRows, focus],
  );
  const dialogRows = useMemo(() => {
    if (!docRow) return [];
    const docFocus = createPedidoFocus("doc", docRow);
    return annotatedRows.filter((row) => rowMatchesPedidoFocus(row, docFocus));
  }, [annotatedRows, docRow]);

  const handleOpenDoc = (row) => {
    setFocus(createPedidoFocus("doc", row));
    setDocRow(row);
  };

  const hasExtraFilters =
    Boolean(search.trim()) ||
    estado !== "todos" ||
    Boolean(cliente) ||
    docKind !== "todos" ||
    showParteDirecto ||
    orden !== PEDIDO_VENTA_ORDEN_DEFAULT ||
    Boolean(focus);

  const clearFilters = () => {
    setSearch("");
    setEstado("todos");
    setCliente("");
    setDocKind("todos");
    setShowParteDirecto(false);
    setOrden(PEDIDO_VENTA_ORDEN_DEFAULT);
    setFocus(null);
    setDocRow(null);
  };

  const handleFocus = (type, row) => {
    setFocus((current) =>
      togglePedidoFocus(current, createPedidoFocus(type, row)),
    );
  };

  const filterChipSx = (active, color) => ({
    height: 28,
    fontWeight: 800,
    fontSize: "0.72rem",
    cursor: "pointer",
    color: active ? color : "text.secondary",
    bgcolor: active ? alpha(color, 0.14) : theme.palette.background.paper,
    border: "1px solid",
    borderColor: active ? alpha(color, 0.4) : theme.palette.divider,
  });

  return (
    <>
      <Box
        sx={{ ...pageHeroShellSx(theme, { sticky: !isMobile }), flexShrink: 0 }}
      >
        <Box
          sx={{
            p: { xs: 1.25, sm: 1.5 },
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "stretch", lg: "center" },
            gap: 1.25,
          }}
        >
          <Stack
            direction="row"
            spacing={1.25}
            alignItems="flex-start"
            sx={{ minWidth: 0, flex: 1 }}
          >
            <Box
              sx={{
                width: { xs: 44, md: 38 },
                height: { xs: 44, md: 38 },
                borderRadius: 2,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: alpha(accent, 0.14),
                color: "primary.dark",
                border: "1px solid",
                borderColor: alpha(accent, 0.28),
              }}
            >
              <LeaderboardOutlinedIcon
                sx={{ fontSize: { xs: "1.2rem", md: "1.05rem" } }}
              />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ lineHeight: 1.1, letterSpacing: 1.1, fontWeight: 700 }}
              >
                SALES · PEDIDOS VENTA
              </Typography>
              <Typography
                variant="h6"
                sx={{ lineHeight: 1.12, fontWeight: 800 }}
              >
                Sales Orders{" "}
                <Box component="span" sx={{ color: "primary.main" }}>
                  · {year}
                </Box>
              </Typography>
              {empresa === "fusion" ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  Consolidado Stage + ERNA
                </Typography>
              ) : null}
              <Stack
                direction="row"
                spacing={0.75}
                useFlexGap
                sx={{ mt: 0.65, flexWrap: "wrap" }}
              >
                <Chip
                  size="small"
                  label={`${summary.count} filas`}
                  sx={{
                    fontWeight: 800,
                    bgcolor: theme.palette.background.paper,
                    border: "1px solid",
                    borderColor: theme.palette.divider,
                  }}
                />
                {parteHiddenCount > 0 ? (
                  <Chip
                    size="small"
                    label={
                      showParteDirecto
                        ? `Venta s/ Grupo · +${summary.parteCount} parte Directo`
                        : `Venta comercial (Grupo) · ${parteHiddenCount} parte Directo oculta`
                    }
                    onClick={() => setShowParteDirecto((v) => !v)}
                    sx={{
                      fontWeight: 700,
                      cursor: "pointer",
                      bgcolor: alpha("#7b1fa2", 0.1),
                      color: "#6a1b9a",
                    }}
                  />
                ) : null}
                <Chip
                  size="small"
                  label={`Venta ${formatPedidoAmount(summary.totalSales) || "0,00"}`}
                  sx={{
                    fontWeight: 700,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: "primary.dark",
                  }}
                />
                <Chip
                  size="small"
                  label={`Profit ${formatPedidoAmount(summary.totalProfit) || "0,00"}`}
                  sx={{
                    fontWeight: 700,
                    bgcolor: alpha(theme.palette.success.main, 0.12),
                    color: "success.dark",
                  }}
                />
                <Chip
                  size="small"
                  label={`Margen ${formatProfitPct(summary.marginPct || 0)}`}
                  sx={{
                    fontWeight: 700,
                    bgcolor: alpha(theme.palette.success.main, 0.08),
                    color: "success.dark",
                  }}
                />
                {summary.pendingUnits > 0 ? (
                  <Chip
                    size="small"
                    clickable
                    onClick={() =>
                      setEstado((current) =>
                        current === "abiertos" ? "todos" : "abiertos",
                      )
                    }
                    label={`Ptes. ${summary.pendingUnits.toLocaleString("es-ES")} uds · ${formatPedidoAmount(summary.pendingAmount) || "0,00"} €`}
                    sx={{
                      fontWeight: 700,
                      bgcolor: alpha(theme.palette.error.main, 0.1),
                      color: "error.dark",
                    }}
                  />
                ) : null}
              </Stack>
            </Box>
          </Stack>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            useFlexGap
            sx={{ width: { xs: "100%", lg: "auto" }, flexShrink: 0 }}
          >
            {onEmpresaChange ? (
              <TextField
                select
                size="small"
                label="Empresa"
                value={empresa}
                onChange={(e) => onEmpresaChange(String(e.target.value))}
                sx={{
                  ...pageFieldSx(theme),
                  minWidth: { xs: "100%", sm: 130 },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BusinessOutlinedIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              >
                {EMPRESA_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            ) : null}
            <TextField
              select
              size="small"
              label="Año"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              sx={{ ...pageFieldSx(theme), minWidth: { xs: "100%", sm: 100 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarMonthOutlinedIcon
                      fontSize="small"
                      color="action"
                    />
                  </InputAdornment>
                ),
              }}
            >
              {getYearOptions().map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </Box>

        <Box
          sx={{
            px: { xs: 1.25, sm: 1.5 },
            pb: 1.25,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 1,
          }}
        >
          <TextField
            size="small"
            placeholder="Buscar cliente, PO, artículo…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{
              ...pageFieldSx(theme),
              minWidth: { xs: "100%", sm: 240 },
              flex: { sm: "1 1 240px" },
            }}
          />
          <Autocomplete
            size="small"
            options={clientes}
            value={cliente || null}
            onChange={(_event, value) => setCliente(value || "")}
            sx={{
              minWidth: { xs: "100%", sm: 240 },
              flex: { sm: "1 1 220px" },
            }}
            renderInput={(params) => (
              <TextField {...params} label="Cliente" sx={pageFieldSx(theme)} />
            )}
          />
          <TextField
            select
            size="small"
            label="Ordenar por"
            value={orden}
            onChange={(e) => setOrden(String(e.target.value))}
            sx={{ ...pageFieldSx(theme), minWidth: { xs: "100%", sm: 230 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SwapVertOutlinedIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          >
            {PEDIDO_VENTA_ORDEN_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <Stack direction="row" spacing={0.6} useFlexGap flexWrap="wrap">
            {ESTADO_OPTIONS.map((opt) => (
              <Chip
                key={opt.value}
                size="small"
                clickable
                label={opt.label}
                onClick={() => setEstado(opt.value)}
                sx={filterChipSx(
                  estado === opt.value,
                  opt.value === "abiertos"
                    ? theme.palette.error.main
                    : opt.value === "servidos"
                      ? theme.palette.success.main
                      : theme.palette.text.primary,
                )}
              />
            ))}
            {DOC_KIND_OPTIONS.map((opt) => (
              <Chip
                key={opt.value}
                size="small"
                clickable
                label={opt.label}
                onClick={() => setDocKind(opt.value)}
                sx={filterChipSx(
                  docKind === opt.value,
                  opt.value === "invoices"
                    ? "#64dd01"
                    : opt.value === "orders"
                      ? "#007FFF"
                      : theme.palette.text.primary,
                )}
              />
            ))}
            {parteHiddenCount > 0 ? (
              <Chip
                size="small"
                clickable
                label={
                  showParteDirecto
                    ? "Ocultar parte Directo"
                    : `Mostrar parte Directo (${parteHiddenCount})`
                }
                onClick={() => setShowParteDirecto((v) => !v)}
                sx={filterChipSx(showParteDirecto, "#7b1fa2")}
              />
            ) : null}
          </Stack>
          {hasExtraFilters ? (
            <Button
              size="small"
              startIcon={<ClearAllIcon />}
              onClick={clearFilters}
              sx={{ textTransform: "none", fontWeight: 800 }}
            >
              Limpiar
            </Button>
          ) : null}
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: "block",
            px: { xs: 1.25, sm: 1.5 },
            pb: 1,
            fontWeight: 600,
          }}
        >
          Importes de venta/margen en € (si el pedido es USD se convierte). Azul
          = pedido sin factura; verde = ya hay factura (en Grupo, cuenta la del
          Directo). La parte Directo va oculta por defecto. Profit 0 = sin
          pedido de compra enlazado en A3.
        </Typography>
      </Box>

      {focus ? (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            mb: 1.25,
            px: 1.25,
            py: 0.85,
            borderRadius: 2,
            border: "1px solid",
            borderColor: alpha(accent, 0.35),
            bgcolor: alpha(accent, 0.08),
          }}
        >
          <Chip
            size="small"
            label={focus.type === "cliente" ? "Cliente" : "PO"}
            sx={{ fontWeight: 800, height: 22 }}
          />
          <Typography
            variant="body2"
            fontWeight={800}
            sx={{ flex: 1, minWidth: 0 }}
          >
            {describePedidoFocus(focus, focusedCount)}
          </Typography>
          <Chip
            size="small"
            clickable
            icon={<CloseIcon sx={{ fontSize: "14px !important" }} />}
            label="Quitar"
            onClick={() => setFocus(null)}
            sx={{ fontWeight: 700, height: 24 }}
          />
        </Stack>
      ) : null}

      {loading ? (
        <Box sx={{ mt: 1.25 }}>
          {isMobile ? (
            <Stack spacing={1}>
              {[1, 2, 3].map((n) => (
                <Skeleton key={n} variant="rounded" height={180} />
              ))}
            </Stack>
          ) : (
            <Skeleton variant="rounded" height={420} />
          )}
        </Box>
      ) : isMobile ? (
        <Box sx={{ mt: 1.25 }}>
          <PedidosVentaMobile
            theme={theme}
            rows={filteredRows}
            focus={focus}
            onFocusRow={handleFocus}
            onOpenDoc={handleOpenDoc}
          />
        </Box>
      ) : (
        <Box
          sx={{
            mt: 1.25,
            ...pageContentCardSx(theme, accent),
            p: 0,
            overflow: "hidden",
            flexShrink: 0,
          }}
        >
          <PedidosVentaTableDesktop
            theme={theme}
            accent={accent}
            rows={filteredRows}
            focus={focus}
            onFocusRow={handleFocus}
            onOpenDoc={handleOpenDoc}
          />
        </Box>
      )}
      <PedidoVentaDocDialog
        open={Boolean(docRow)}
        row={docRow}
        rows={dialogRows}
        onClose={() => setDocRow(null)}
      />
    </>
  );
};

export default TablaCompleta;
